import { useProjectActivity } from '../../../api/projects.js';
import { AuditEventList } from '../../../components/audit/AuditEventList.js';

interface ActivityPaneProps {
  projectId: string;
}

function ClipboardIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 4H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="3" rx="1" />
      <path d="M8.5 11h7M8.5 15h4.5" />
    </svg>
  );
}

export function ActivityPane({ projectId }: ActivityPaneProps) {
  const { data, isLoading, isError } = useProjectActivity(projectId);
  const events = data?.events ?? [];

  // The whole pane mounts fresh when the Activity tab is selected, so a
  // separate "persistent" sr-only live region wouldn't actually pre-exist the
  // first (loading) transition. Instead each visible state node carries its own
  // live-region role — role="status"/"alert" are announced on insertion, which
  // is the reliable pattern for SPA status messages (and avoids double-announce).
  return (
    <>
      {isLoading && (
        <div className="pane-loading" role="status">
          Loading activity…
        </div>
      )}

      {isError && (
        <div className="pane-error" role="alert">
          Failed to load activity.
        </div>
      )}

      {!isLoading && !isError && events.length === 0 && (
        <div className="activity-placeholder" role="status">
          <span className="activity-placeholder__icon" aria-hidden="true">
            <ClipboardIcon />
          </span>
          <p style={{ margin: 0, fontSize: 'var(--fs-16)', fontWeight: 'var(--fw-medium)' }}>
            No activity yet
          </p>
          <p style={{ margin: 0, fontSize: 'var(--fs-14)', color: 'var(--fg3)' }}>
            Changes to this project, its tasks, and assignments will show up here.
          </p>
        </div>
      )}

      {!isLoading && !isError && events.length > 0 && (
        <div role="region" aria-label="Project activity">
          <AuditEventList events={events} />
        </div>
      )}
    </>
  );
}
